import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { NAV_MIN_GAP_TO_LOGO_PX } from '../constants/headerNavConstants';
import { computeVisibleCount } from '../services/navOverflowService';

export interface UseNavOverflowResult {
  // Wraps the visible list; its width is the room the navigation has.
  navRef: RefObject<HTMLElement | null>;
  // Hidden copy of the complete list, used only for measuring.
  ghostRef: RefObject<HTMLUListElement | null>;
  // How many of the leading items fit; the rest belong in the overflow menu.
  visibleCount: number;
}

// Decides how many navigation items fit instead of assuming fixed viewport
// breakpoints: everything stays visible as long as there is room, and items
// fold into the overflow menu only once the bar would crowd the logo.
//
// The trigger is the navigation element's own size, not the window's: anything
// that reflows the header (a window resize, but also a longer location name or
// a scrollbar appearing) changes the room available and has to be reacted to.
//
// Widths come from a hidden copy of the full list rather than from the visible
// one, so the measurements are always of every item at its real width, in the
// current language and font, however many items are currently folded away.
export function useNavOverflow(itemCount: number): UseNavOverflowResult {
  const { i18n } = useTranslation();
  const navRef = useRef<HTMLElement | null>(null);
  const ghostRef = useRef<HTMLUListElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);
  // Bumped when the room or the labels change, to re-run the measurement.
  const [measureToken, setMeasureToken] = useState(0);

  const remeasure = useCallback(() => {
    setMeasureToken((token) => token + 1);
  }, []);

  // Labels measured against a fallback font have the wrong width.
  useEffect(() => {
    let isCurrent = true;
    void document.fonts?.ready.then(() => {
      if (isCurrent) remeasure();
    });
    return () => {
      isCurrent = false;
    };
  }, [remeasure]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(remeasure);
    observer.observe(nav);
    return () => {
      observer.disconnect();
    };
  }, [remeasure]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const ghost = ghostRef.current;
    if (!nav || !ghost) return;
    // Not laid out yet (or hidden): measuring now would fold everything away.
    if (nav.clientWidth === 0) return;

    const measured = Array.from(ghost.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement
    );
    if (measured.length <= itemCount) return;

    const fitting = computeVisibleCount({
      itemWidths: measured.slice(0, itemCount).map((child) => child.offsetWidth),
      // Anything after the items stands in for the overflow trigger.
      triggerWidth: measured
        .slice(itemCount)
        .reduce((sum, child) => sum + child.offsetWidth, 0),
      gap: Number.parseFloat(getComputedStyle(ghost).columnGap) || 0,
      budget: nav.clientWidth - NAV_MIN_GAP_TO_LOGO_PX
    });

    // Functional form so the effect needs no dependency on its own output,
    // which would cost a second measuring pass after every fold.
    setVisibleCount((current) => (fitting === current ? current : fitting));
  }, [itemCount, i18n.language, measureToken]);

  return { navRef, ghostRef, visibleCount };
}
